import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { getTour, TOURS } from './registry';
import { loadProgress, saveProgress, type ProgressMap } from './storage';
import type { TourProgress, TourStatus } from './types';
import TourOverlay from './TourOverlay';

interface TourContextValue {
  activeTourId: string | null;
  currentStep: number;
  isRunning: boolean;
  // Begin a tour from the start (or from saved progress when resume=true).
  startTour: (id: string, opts?: { resume?: boolean }) => void;
  resumeTour: (id: string) => void;
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
  // Close the overlay but keep progress so the tour can be resumed later.
  pause: () => void;
  // Mark the tour complete and close.
  finish: () => void;
  getStatus: (id: string) => TourStatus;
  getProgress: (id: string) => TourProgress | undefined;
}

const TourContext = createContext<TourContextValue | undefined>(undefined);


export function TourProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { userInfo } = useUser();

  // A stable per-user key so progress follows the account, not the browser.
  const userKey = userInfo?.subject || userInfo?.email || 'anon';

  const [progress, setProgress] = useState<ProgressMap>({});
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Reload persisted progress whenever the active user changes.
  useEffect(() => {
    setProgress(loadProgress(userKey));
  }, [userKey]);

  // When a tour closes (pause or finish), force the ENTIRE document to
  // RE-COMPOSITE. Removing a full-screen fixed overlay doesn't reliably
  // invalidate Chrome's compositing layers, so the dim stayed "burned in" over
  // the page until a manual refresh.
  //
  // This MUST use `visibility`, not `display`. An earlier version toggled
  // `body.style.display = 'none'`, which removes body (and everything in it,
  // including the h-14 header) from LAYOUT for that instant — every box
  // collapses to zero size. Anything that measures the header during that
  // window (a ResizeObserver, the tour's own rect-tracking in
  // useTargetRect/TourOverlay, even React's own effect scheduling) can observe
  // that zero-height frame and get stuck rendering it, which is exactly the
  // "header frozen half-height" bug. `visibility: hidden` forces the same full
  // repaint (it removes the subtree from painting/hit-testing) WITHOUT ever
  // removing it from layout — every element, including the header, keeps its
  // real computed size throughout, so nothing can observe a bad measurement.
  //
  // One more side effect must be neutralised: the tour's scrollIntoView starts
  // a SMOOTH scroll that keeps animating and fights the user after they pause.
  // We force scroll-behavior:auto and re-assert the saved scrollTop, which
  // cancels the in-flight animation instantly.
  const wasActiveRef = useRef(false);
  useLayoutEffect(() => {
    const isActive = activeTourId !== null;
    const justClosed = wasActiveRef.current && !isActive;
    wasActiveRef.current = isActive;
    if (!justClosed) return;

    const body = document.body;
    const scroller = document.getElementById('app-scroll');
    const savedScrollTop = scroller?.scrollTop ?? 0;

    const prevBehavior = scroller?.style.scrollBehavior ?? '';
    if (scroller) scroller.style.scrollBehavior = 'auto';

    const prevVisibility = body.style.visibility;
    body.style.visibility = 'hidden';
    void body.offsetHeight; // force a synchronous repaint while hidden — layout is untouched
    body.style.visibility = prevVisibility;

    if (scroller) {
      scroller.scrollTop = savedScrollTop; // cancels the in-flight smooth scroll
      requestAnimationFrame(() => {
        scroller.style.scrollBehavior = prevBehavior;
      });
    }
  }, [activeTourId]);

  // Persist a single tour's progress and mirror it into local state.
  const persist = useCallback(
    (id: string, patch: TourProgress) => {
      setProgress((prev) => {
        const nextMap: ProgressMap = { ...prev, [id]: patch };
        saveProgress(userKey, nextMap);
        return nextMap;
      });
    },
    [userKey],
  );

  const activeTour = activeTourId ? getTour(activeTourId) : undefined;

  const startTour = useCallback(
    (id: string, opts?: { resume?: boolean }) => {
      const tour = getTour(id);
      if (!tour || tour.comingSoon || tour.steps.length === 0) return;

      const saved = loadProgress(userKey)[id];
      const startIndex =
        opts?.resume && saved && saved.stepIndex < tour.steps.length
          ? saved.stepIndex
          : 0;

      // If the tour lives on another page, take the user there first.
      const stepRoute = tour.steps[startIndex]?.route || tour.route;
      if (stepRoute && window.location.pathname !== stepRoute) {
        navigate(stepRoute);
      }

      setActiveTourId(id);
      setCurrentStep(startIndex);
      persist(id, { status: 'in_progress', stepIndex: startIndex, updatedAt: Date.now() });
    },
    [navigate, persist, userKey],
  );

  const resumeTour = useCallback((id: string) => startTour(id, { resume: true }), [startTour]);

  const goTo = useCallback(
    (index: number) => {
      if (!activeTour) return;
      const clamped = Math.max(0, Math.min(index, activeTour.steps.length - 1));
      const step = activeTour.steps[clamped];
      if (step?.route && window.location.pathname !== step.route) {
        navigate(step.route);
      }
      setCurrentStep(clamped);
      persist(activeTour.id, {
        status: 'in_progress',
        stepIndex: clamped,
        updatedAt: Date.now(),
      });
    },
    [activeTour, navigate, persist],
  );

  const finish = useCallback(() => {
    if (!activeTour) return;
    persist(activeTour.id, {
      status: 'completed',
      stepIndex: activeTour.steps.length,
      updatedAt: Date.now(),
    });
    setActiveTourId(null);
    setCurrentStep(0);
  }, [activeTour, persist]);

  const next = useCallback(() => {
    if (!activeTour) return;
    if (currentStep >= activeTour.steps.length - 1) {
      finish();
    } else {
      goTo(currentStep + 1);
    }
  }, [activeTour, currentStep, finish, goTo]);

  const prev = useCallback(() => {
    if (currentStep > 0) goTo(currentStep - 1);
  }, [currentStep, goTo]);

  const pause = useCallback(() => {
    if (activeTour) {
      persist(activeTour.id, {
        status: 'paused',
        stepIndex: currentStep,
        updatedAt: Date.now(),
      });
    }
    setActiveTourId(null);
  }, [activeTour, currentStep, persist]);

  const getStatus = useCallback(
    (id: string): TourStatus => progress[id]?.status ?? 'not_started',
    [progress],
  );

  const getProgress = useCallback(
    (id: string): TourProgress | undefined => progress[id],
    [progress],
  );

  const value = useMemo<TourContextValue>(
    () => ({
      activeTourId,
      currentStep,
      isRunning: activeTourId !== null,
      startTour,
      resumeTour,
      next,
      prev,
      goTo,
      pause,
      finish,
      getStatus,
      getProgress,
    }),
    [
      activeTourId,
      currentStep,
      startTour,
      resumeTour,
      next,
      prev,
      goTo,
      pause,
      finish,
      getStatus,
      getProgress,
    ],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeTour && activeTour.steps.length > 0 && (
        <TourOverlay
          tour={activeTour}
          stepIndex={currentStep}
          onNext={next}
          onPrev={prev}
          onPause={pause}
          onFinish={finish}
        />
      )}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
}

// Re-export so pages can enumerate tours without a second import.
export { TOURS };
